import { ref } from "vue";
import type { HabToast, HabToastTone } from "@livereader/habitus-vue";

/**
 * The Operator's one toast stack.
 *
 * ONE STACK FOR THE SECTION, not one per view. A result is news about what the user
 * just did — a process archived, a role saved, a restart that failed — and it has to
 * survive the navigation that often follows it: delete a dataspace from its settings
 * and you land on the list, which is exactly where "Deleted" should be read.
 *
 * TWO HOSTS RENDER IT, never at once. `OperatorLayout` owns one for everything under
 * `/operator`, the reason `DesignerLayout` owns the Designer's. The dataspace list is
 * the exception: it lives at `/`, inside the system section's landing layout, which
 * cannot import from here — so `HomeView` renders its own host over the same stack.
 * Because the stack is module state rather than either host's, a toast pushed on one
 * side of that navigation is still there on the other.
 *
 * Module state rather than a store: nothing reads it but the hosts, and a toast is
 * gone the moment it is dismissed — there is nothing to persist or to reset.
 */
const toasts = ref<HabToast[]>([]);
let lastId = 0;

export function useOperatorToasts() {
	function push(message: string, tone: HabToastTone = "info") {
		toasts.value.push({ id: ++lastId, message, tone });
	}

	return {
		toasts,
		push,
		success: (message: string) => push(message, "success"),
		error: (message: string) => push(message, "error"),
		dismiss: (id: string | number) => {
			toasts.value = toasts.value.filter((toast) => toast.id !== id);
		},
	};
}
